import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useCustomerBasketAdapter } from "@shared-core/data/CustomerBasketAdapterContext";
import type {
  CustomerBasketView,
  CustomerCheckoutDeliveries,
  CustomerCollectionPoint,
  CustomerPaymentMethod,
} from "@shared-core/data/customer-basket-types";
import {
  deliveryChoicesOf,
  NO_PICK,
  type DeliveryPick,
} from "@shared-core/domain/checkout-delivery";
import { poFormatHint } from "@shared-core/domain/credit-control-presentation";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { isHttpUrl, sameAppPath } from "@shared-core/domain/url";
import {
  customerBasketFailure,
  deliveryErrorsByRef,
  type CustomerBasketFailure,
} from "@shared-core/domain/customer-basket-presentation";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import { BasketGate } from "./BasketGate";
import { BasketTotals } from "./BasketTotals";
import {
  CheckoutDeliverySection,
  type DeliveryErrors,
  type OnDeliveryPick,
} from "./CheckoutDeliverySection";
import { PaymentMethodChoice } from "./PaymentMethodChoice";
import { useCustomerBasket } from "./CustomerBasketProvider";
import { Loader2 } from "lucide-react";

// Delivery, method, purchase order number, needed-by date and Place order, over the same basket view the basket page
// reads (docs/features/customer-portal-ordering/17 section 3.3), in the quote page's layout: the lines listed
// once on the page, and the money and Place order in one summary beside it (a slim bar and sheet on a phone).
// The Delivery section (order to invoice doc 104 §9.3) is the server's preview under the person's picks; its totals
// replace the basket's once read, and Place order sends the same picks.
// The contact is the person placing the order, shown and fixed (B3). The checkout key is minted once per visit
// to this page and reused for every retry of the attempt, so a double click or a retry after a dropped
// connection returns the same order.

const FORM_ID = "checkout-form";

function newCheckoutKey(): string {
  return crypto.randomUUID();
}

export function PortalCheckoutPage() {
  const canCheckOut = useCustomerBasket()?.capabilities.checkout ?? false;
  return (
    <BasketGate>
      {(basket, refresh) =>
        // Nothing to place: an empty basket, a build that cannot place, a basket the server will not take as it
        // stands (the basket page says why), or one with no place-order URL.
        !canCheckOut ||
        basket.lines.length === 0 ||
        basket.checkout === null ||
        basket.blocked_reason !== null ||
        basket.actions.place_order_url === null ? (
          <Navigate to={paths.portalBasket()} replace />
        ) : (
          <CheckoutForm basket={basket} refresh={refresh} />
        )
      }
    </BasketGate>
  );
}

export function CheckoutForm({
  basket,
  refresh,
}: {
  basket: CustomerBasketView;
  refresh: () => void;
}) {
  const checkout = basket.checkout!;
  const placeOrderUrl = basket.actions.place_order_url!;
  const baskets = useCustomerBasketAdapter();
  const capabilities = usePlatformCapabilities();
  const navigate = useNavigate();
  const [checkoutKey] = useState(newCheckoutKey);

  const [method, setMethod] = useState<CustomerPaymentMethod | null>(
    checkout.default_method,
  );
  const [orderNumber, setOrderNumber] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<CustomerBasketFailure | null>(null);
  // The lines whose prices moved under this attempt, named as the basket named them before it was re-read.
  const [changedLines, setChangedLines] = useState<ChangedLine[]>([]);
  // The deliveries whose price is no longer the one shown, in the server's words, until the person acts again.
  const [movedDeliveryPrices, setMovedDeliveryPrices] = useState<
    Record<string, string>
  >({});
  const failureRef = useRef<HTMLDivElement>(null);
  // A refusal is announced at the top of the page; on a phone the person is at the bar, so bring them to it.
  useEffect(() => {
    if (failure) failureRef.current?.focus();
  }, [failure]);

  const poVisible = checkout.po_rule !== "not_required";
  const poHint = poFormatHint(checkout);

  // The Delivery section: the person's picks, and the server's answer to them (read again on each change).
  const [picks, setPicks] = useState<Record<string, DeliveryPick>>({});
  const [previewed, setPreviewed] = useState<CustomerCheckoutDeliveries | null>(
    null,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  // The server's refusals of a delivery's inputs, by delivery, until the person changes a pick.
  const [deliveryErrors, setDeliveryErrors] = useState<DeliveryErrors>({});
  const picksRef = useRef(picks);
  // The deliveries exactly as the section on screen shows them: what Place order says it showed.
  const previewedRef = useRef(previewed);
  previewedRef.current = previewed;
  const pointsRef = useRef<CustomerCollectionPoint[]>([]);
  const previewSeq = useRef(0);
  // Null where the read names no preview route: the page then has no Delivery section and shows the basket's totals.
  const previewUrl = basket.actions.preview_deliveries_url;
  const preview = useCallback(
    (nextPicks: Record<string, DeliveryPick>) => {
      if (previewUrl === null) return;
      // Only the latest answer counts: an earlier one arriving late would show choices the person has changed.
      const seq = ++previewSeq.current;
      baskets
        .previewCheckoutDeliveries(
          previewUrl,
          deliveryChoicesOf(nextPicks, pointsRef.current, "preview"),
        )
        .then((answer) => {
          if (seq !== previewSeq.current) return;
          pointsRef.current = answer.collection_points;
          setPreviewed(answer);
          setPreviewError(null);
        })
        .catch((err: unknown) => {
          if (seq !== previewSeq.current) return;
          setPreviewError(customerBasketFailure(err).summary);
        });
    },
    [baskets, previewUrl],
  );
  // Read on arrival, and again when the basket itself is re-read (a price that moved), keeping the picks.
  useEffect(() => preview(picksRef.current), [preview, basket]);

  const pickDelivery: OnDeliveryPick = (deliveryRef, patch, options) => {
    const next = {
      ...picksRef.current,
      [deliveryRef]: {
        ...(picksRef.current[deliveryRef] ?? NO_PICK),
        ...patch,
      },
    };
    picksRef.current = next;
    setPicks(next);
    setDeliveryErrors({});
    setMovedDeliveryPrices({});
    if (options?.preview !== false) preview(next);
  };

  const methods = checkout.payment_methods;
  // A delivery no one has priced holds the whole order (order to invoice doc 107): only staff can agree a delivery
  // price, so Place order waits, and the delivery itself says why.
  const isDeliveryUnpriced = previewed?.has_delivery_not_yet_priced ?? false;
  const isUsable = (m: CustomerPaymentMethod | null) =>
    methods.some((offer) => offer.method === m && offer.is_usable);
  const chosenMethod = isUsable(method)
    ? method
    : (methods.find((offer) => offer.is_usable)?.method ?? null);

  async function place() {
    if (chosenMethod === null) return;
    setSubmitting(true);
    setFailure(null);
    setChangedLines([]);
    setMovedDeliveryPrices({});
    setDeliveryErrors({});
    try {
      const placed = await baskets.placeOrder(placeOrderUrl, {
        payment_method: chosenMethod,
        customer_order_number: orderNumber.trim() || null,
        required_on: neededBy || null,
        checkout_key: checkoutKey,
        deliveries: deliveryChoicesOf(
          picksRef.current,
          pointsRef.current,
          "place",
          previewedRef.current?.deliveries ?? [],
        ),
      });
      if (placed.checkout_url && isHttpUrl(placed.checkout_url)) {
        // The card payment page (a web address, never another scheme); it returns to the order's page.
        capabilities.redirectTo(placed.checkout_url);
        return;
      }
      // The signed-in order page: the server names it, and only a path inside this app is followed.
      navigate(
        sameAppPath(placed.next_path) ??
          paths.portalOrderDetail({ orderId: placed.order_no }),
      );
      refresh();
    } catch (err) {
      const next = customerBasketFailure(err);
      setFailure(next);
      setDeliveryErrors(
        deliveryErrorsByRef(next.deliveryErrors, Object.keys(picksRef.current)),
      );
      if (next.changedPrices) {
        setChangedLines(
          next.changedPrices.map((c) => ({
            title: basket.lines[c.position - 1]?.title ?? `Item ${c.position}`,
            oldPricePence: c.oldPricePence,
            newPricePence: c.newPricePence,
          })),
        );
        // The basket now holds the new prices; read it again so the summary shows them.
        refresh();
      }
      if (next.changedDeliveries && next.changedDeliveries.length > 0) {
        setMovedDeliveryPrices(
          Object.fromEntries(
            next.changedDeliveries.map((c) => [c.deliveryRef, c.message]),
          ),
        );
        // A delivery price is read, not stored: read the section again so it shows what the order will charge, with
        // the person's choices as they were. They place again when they have looked; nothing is sent for them.
        preview(picksRef.current);
      }
      setSubmitting(false);
    }
  }

  const placeButton = (
    <Button
      type="submit"
      form={FORM_ID}
      size="lg"
      className="w-full"
      disabled={submitting || chosenMethod === null || isDeliveryUnpriced}
    >
      {submitting && <Loader2 className="animate-spin" />}
      {submitting ? "Placing your order…" : "Place order"}
    </Button>
  );

  return (
    <div className="space-y-5">
      <div>
        <Link
          to={paths.portalBasket()}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Back to your basket
        </Link>
        <h1 className="text-xl font-semibold tracking-tight">Check out</h1>
        <p className="text-sm text-muted-foreground">
          For {basket.customer_name}
        </p>
      </div>

      {failure && (
        <div
          ref={failureRef}
          tabIndex={-1}
          role="alert"
          className="space-y-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 focus:outline-none dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
        >
          <p>{failure.summary}</p>
          {changedLines.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5">
              {changedLines.map((c) => (
                <li key={c.title} className="tabular-nums">
                  {c.title}: {formatQuotedPrice(c.oldPricePence)} is now{" "}
                  {formatQuotedPrice(c.newPricePence)} (ex VAT)
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Two independent columns from lg up (left: contact, order, totals; right: delivery, payment, details,
          Place order), so neither leaves a gap for the other's height. On a phone the two column wrappers
          disappear (`contents`) and `order` interleaves their items into one stack: contact, order, delivery,
          payment, details, the totals, Place order. Everything that can take focus is in the right column and
          keeps its order; only the static totals are read at a different point on a phone than they are drawn. */}
      <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(24rem,1fr)] lg:items-start lg:gap-x-8">
        <div className="contents lg:block lg:min-w-0 lg:space-y-5">
          <Card className="order-1 gap-1 p-5 text-sm">
            <h2 className="text-lg font-semibold">Who we&apos;ll contact</h2>
            <p className="font-medium">{checkout.contact_name}</p>
            <p>{checkout.contact_email}</p>
            <p className="text-muted-foreground">
              We&apos;ll send updates and your proof to this address.
            </p>
          </Card>

          <Card className="order-2 gap-3 p-5">
            <h2 className="text-lg font-semibold">Your order</h2>
            <ul className="divide-y text-sm">
              {basket.lines.map((line) => (
                <li
                  key={line.line_id}
                  className="flex justify-between gap-3 py-2 first:pt-0 last:pb-0"
                >
                  <span>
                    <span className="block font-medium">{line.title}</span>
                    {line.spec && (
                      <span className="block text-muted-foreground">
                        {line.spec}
                      </span>
                    )}
                    <span className="block text-muted-foreground tabular-nums">
                      {line.quantity.toLocaleString("en-GB")}
                    </span>
                    {line.dispatch_note && (
                      <span className="block text-muted-foreground">
                        {line.dispatch_note}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {line.total_inc_vat_pence === null
                      ? ""
                      : formatQuotedPrice(line.total_inc_vat_pence)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          {/* The delivery choice moves these totals, so they update as it changes. */}
          <aside
            aria-label="Your total and placing your order"
            className="order-6"
          >
            <Card className="gap-2 p-5">
              <h2 className="text-lg font-semibold">Order summary</h2>
              {/* The preview's totals once read: delivery and VAT as the order will charge them. */}
              <BasketTotals totals={previewed ?? basket.totals} />
            </Card>
          </aside>
        </div>

        <div className="contents lg:block lg:min-w-0 lg:space-y-5">
          <div className="order-3 min-w-0 space-y-5">
            {previewUrl !== null && (
              <CheckoutDeliverySection
                previewed={previewed}
                error={previewError}
                picks={picks}
                errors={deliveryErrors}
                movedPrices={movedDeliveryPrices}
                disabled={submitting}
                onPick={pickDelivery}
              />
            )}

            <form
              id={FORM_ID}
              className="space-y-5"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void place();
              }}
            >
              <Card className="gap-3 p-5">
                <h2 className="text-lg font-semibold">
                  How would you like to pay?
                </h2>
                <PaymentMethodChoice
                  methods={methods}
                  value={chosenMethod}
                  onChange={setMethod}
                  disabled={submitting}
                  errorId={
                    failure?.fieldErrors.payment_method
                      ? "payment-method-error"
                      : undefined
                  }
                />
                {failure?.fieldErrors.payment_method && (
                  <p
                    id="payment-method-error"
                    className="text-sm text-red-700 dark:text-red-400"
                  >
                    {failure.fieldErrors.payment_method}
                  </p>
                )}
              </Card>

              <Card className="gap-3 p-5">
                <h2 className="text-lg font-semibold">Order details</h2>
                <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
                  {poVisible && (
                    <div className="space-y-1.5">
                      <Label htmlFor="customer-order-number">
                        Your purchase order number
                      </Label>
                      <Input
                        id="customer-order-number"
                        value={orderNumber}
                        disabled={submitting}
                        autoComplete="off"
                        aria-invalid={
                          failure?.fieldErrors.customer_order_number
                            ? true
                            : undefined
                        }
                        aria-describedby={
                          poHint
                            ? "customer-order-number-hint customer-order-number-format"
                            : "customer-order-number-hint"
                        }
                        onChange={(event) => setOrderNumber(event.target.value)}
                      />
                      {poHint && (
                        <p
                          id="customer-order-number-format"
                          className="text-sm text-muted-foreground"
                        >
                          {poHint}
                        </p>
                      )}
                      <p
                        id="customer-order-number-hint"
                        className={
                          failure?.fieldErrors.customer_order_number
                            ? "text-sm text-red-700 dark:text-red-400"
                            : "text-sm text-muted-foreground"
                        }
                      >
                        {failure?.fieldErrors.customer_order_number ?? (
                          <>
                            <span className="font-medium text-foreground">
                              {checkout.po_rule_label}.
                            </span>{" "}
                            {checkout.po_rule_note}
                          </>
                        )}
                      </p>
                    </div>
                  )}
                  {checkout.can_set_required_on && (
                    <div className="space-y-1.5">
                      <Label htmlFor="required-on">
                        When do you need it? (optional)
                      </Label>
                      <Input
                        id="required-on"
                        type="date"
                        className="w-full"
                        value={neededBy}
                        disabled={submitting}
                        aria-invalid={
                          failure?.fieldErrors.required_on ? true : undefined
                        }
                        aria-describedby={
                          failure?.fieldErrors.required_on
                            ? "required-on-error"
                            : undefined
                        }
                        onChange={(event) => setNeededBy(event.target.value)}
                      />
                      {failure?.fieldErrors.required_on && (
                        <p
                          id="required-on-error"
                          className="text-sm text-red-700 dark:text-red-400"
                        >
                          {failure.fieldErrors.required_on}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </form>
          </div>

          {/* The end of the form the person fills in, so the last thing in the right column. */}
          <div className="order-7">{placeButton}</div>
        </div>
      </div>
    </div>
  );
}

interface ChangedLine {
  title: string;
  oldPricePence: number;
  newPricePence: number;
}
